import { memo, useMemo } from "react";
import { classNames } from "helpers";
import { TextField } from "ui/components/TextField/TextField";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import TextArea from "ui/components/TextField/TextArea/TextArea";
import { Errors } from "services/forms/validation/types";
import { ListingEditFormValues, MarketplacePayment } from "../../../types";
import { ChipGroupTitle, ChipToggle, SectionCard } from "../primitives";
import { DESCRIPTION_MAX_LENGTH, PAYMENT_OPTIONS, TITLE_MAX_LENGTH } from "services/api/sales/listings/consts";
import { applyListingTemplate } from "../../../mapping/template";

interface Props {
  formValues: ListingEditFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<ListingEditFormValues>>;
  payment: MarketplacePayment;
  onPaymentChange: (patch: Partial<MarketplacePayment>) => void;
  errors: Errors;
}

const AdDataSection: React.FC<Props> = ({ formValues, setFormValues, payment, onPaymentChange, errors }) => {
  const handleBaseField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const togglePayment = (code: string) => {
    onPaymentChange({ options: { ...payment.options, [code]: !payment.options[code] } });
  };

  const templateOptions = useMemo(
    () => [
      { value: "", text: "Ručni unos (bez predloška)" },
      ...formValues.templates.map((template) => ({ value: template.id, text: template.name })),
    ],
    [formValues.templates]
  );

  // Odabir predloška prepisuje naslov i opis s popunjenim varijablama vozila.
  const handleTemplateChange = (_e: any, _name: string, item: { value: string }) => {
    setFormValues((prev) => {
      const template = prev.templates.find((tpl) => tpl.id === item.value);

      return {
        ...prev,
        templateId: item.value,
        ...(template ? applyListingTemplate(template, prev.vehicle) : {}),
      };
    });
  };

  const titleLength = formValues.baseTitle.length;
  const descLength = formValues.baseDescription.length;

  return (
    <SectionCard title="Podaci oglasa">
      <div className="flex flex-col gap-4">
        {/* Naslov i opis mogu doći iz predloška ili se upisati ručno. */}
        <div>
          <Dropdown
            name="templateId"
            label="Predložak oglasa"
            value={formValues.templateId}
            options={templateOptions}
            onChange={handleTemplateChange}
            disabled={formValues.templates.length === 0}
          />
          <div className="text-xs text-slate-400 mt-1">
            {formValues.templates.length > 0
              ? "Odabir predloška prepisuje naslov i opis; nakon toga ih možete slobodno urediti."
              : "Nema spremljenih predložaka — dodajte ih u Postavke → Zadane postavke oglasa."}
          </div>
        </div>

        <div>
          <TextField
            type="text"
            name="baseTitle"
            label="Naslov oglasa"
            placeholder="npr. Škoda Kodiaq RS – prvi vlasnik, redovno serviran"
            value={formValues.baseTitle}
            onChange={handleBaseField}
            error={errors.baseTitle}
            required
          />
          <div className={classNames(
            "text-xs mt-1",
            titleLength > TITLE_MAX_LENGTH ? "text-red-500" : "text-slate-400"
          )}>
            {titleLength} / {TITLE_MAX_LENGTH} znakova
          </div>
        </div>

        <div>
          <TextArea
            name="baseDescription"
            label="Opis"
            placeholder="Dodatne informacije o vozilu — stanje, povijest servisa, oprema..."
            rows={40}
            value={formValues.baseDescription}
            onChange={handleBaseField}
            error={errors.baseDescription}
            required
          />
          <div className={classNames(
            "text-xs mt-1",
            descLength > DESCRIPTION_MAX_LENGTH ? "text-red-500" : "text-slate-400"
          )}>
            {descLength} / {DESCRIPTION_MAX_LENGTH} znakova
          </div>
        </div>

        <div>
          <ChipGroupTitle>Mogućnost plaćanja</ChipGroupTitle>
          <div className="flex flex-wrap gap-2">
            {PAYMENT_OPTIONS.map((opt) => (
              <ChipToggle
                key={opt.code}
                label={opt.label}
                selected={!!payment.options[opt.code]}
                onToggle={() => togglePayment(opt.code)}
              />
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default memo(AdDataSection);
